import { Box, Container } from '@mui/material';
import { Dayjs } from 'dayjs';
import { Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import { FunctionalAsset } from '@synergy/models';
import { PoolConfirmationModal, PoolRow, PoolTypography, useIsMobile } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { ScrollToFieldError } from '@synergy/components';
import { useNavigate } from 'react-router';

import { OrderData } from './order-data';
import { PoolPageHeader } from '../../components/page-header/page-header';
import { ROUTES } from '../../utils/routes';
import { ShoppingCartFooter } from './shopping-cart-footer';
import { ShoppingCartItemStatus, ShoppingCartSupplier } from '../../models/shopping-cart';
import { ShoppingCartPageNotifications } from './shopping-cart-page-notifications';
import { SupplierTable } from './supplier-table/supplier-table';
import { TopBar } from './top-bar';
import { cartInfoSelector, orderDataSelector, shoppingCartSelector } from '../../store/shopping-cart-selectors';
import { fetchShoppingCart, refreshAndSubmitShoppingCart } from '../../store/shopping-cart-actions';
import { getDeliveryOptions } from '../../store/shopping-cart-delivery-options-actions';
import { orderDataValidationSchema } from './order-data-validation-schema';
import { useAppDispatch, useAppSelector } from '../../../store/store';

export interface OrderDataFormValues {
    invoiceAddressId?: number | null;
    deliveryAddressId?: number | null;
    avisoContactId?: number;
    deliveryDate: Dayjs | null;
    orderReference: string;
    costCentre: string;
    hasOrderLimit: boolean;
    functionalAssets: (FunctionalAsset | null)[];
}

export const ShoppingCartPage = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const cart = useAppSelector(shoppingCartSelector);
    const cartInfos = useAppSelector(cartInfoSelector);
    const orderData = useAppSelector(orderDataSelector);
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [isArticleNotAvailableDialogOpen, setArticleNotAvailableDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        dispatch(fetchShoppingCart());
        dispatch(getDeliveryOptions());
    }, [dispatch]);

    useEffect(() => {
        if (cart?.suppliers.length === 0) {
            navigate(ROUTES.cart.empty);
        }
    }, [cart, navigate]);

    const initialValues: OrderDataFormValues = {
        invoiceAddressId: orderData.selectedInvoiceAddressId,
        deliveryAddressId: orderData.selectedDeliveryAddressId,
        avisoContactId: orderData.selectedAvisoContactId,
        deliveryDate: orderData.desiredDeliveryDate,
        orderReference: orderData.orderReference,
        costCentre: orderData.costCentre,
        hasOrderLimit: orderData.hasOrderLimit,
        functionalAssets: cartInfos.functionalAssets,
    };

    const handleSubmit = async (values: OrderDataFormValues): Promise<void> => {
        if (
            cart?.itemStatuses?.includes(ShoppingCartItemStatus.Removed) ||
            cart?.itemStatuses?.includes(ShoppingCartItemStatus.PriceByRequest)
        ) {
            setArticleNotAvailableDialog(true);
            return;
        }
        setIsSubmitting(true);
        if (await dispatch(refreshAndSubmitShoppingCart())) {
            navigate(ROUTES.cart.submit, { state: { hasOrderLimit: values.hasOrderLimit } });
        }
        setIsSubmitting(false);
    };

    return (
        <>
            <PoolConfirmationModal
                open={isArticleNotAvailableDialogOpen}
                isSuccessDialog={true}
                title={<FormattedMessage id="app.cart.submit.articleNotAvailable" />}
                action={<FormattedMessage id="app.cart.submit.articleNotAvailable.confirm" />}
                onConfirm={() => setArticleNotAvailableDialog(false)}
            >
                <FormattedMessage id="app.cart.submit.articleNotAvailable.confirmationText" />
            </PoolConfirmationModal>

            <PoolPageHeader variant="white">
                <PoolRow alignItems="baseline">
                    <FormattedMessage id="app.cart" />
                    <PoolTypography variant="h6">({cartInfos?.itemsInCart || 0})</PoolTypography>
                </PoolRow>
            </PoolPageHeader>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={orderDataValidationSchema}
                enableReinitialize={true}
            >
                <Form noValidate={true}>
                    <ScrollToFieldError />
                    <Box sx={{ backgroundColor: theme => theme.palette.grey[100], width: '100%', pt: 3, pb: 1.5 }}>
                        <Container maxWidth="xl">
                            <OrderData />
                        </Container>
                    </Box>

                    <Container maxWidth="xl" sx={{ mt: 2 }}>
                        <ShoppingCartPageNotifications />

                        <TopBar />

                        {!isMobile &&
                            cart?.suppliers.map((supplier: ShoppingCartSupplier) => {
                                return <SupplierTable supplier={supplier} key={supplier.id} />;
                            })}
                    </Container>

                    {isMobile &&
                        cart?.suppliers.map((supplier: ShoppingCartSupplier) => {
                            return <SupplierTable supplier={supplier} key={supplier.id} />;
                        })}

                    <ShoppingCartFooter isSubmitting={isSubmitting} />
                </Form>
            </Formik>
        </>
    );
};
